import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createSlugWithId } from '../utils/slugUtils';
import { 
  RectangleStackIcon,
  PuzzlePieceIcon,
  SparklesIcon,
  ClockIcon,
  ChartBarIcon,
  PlayIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  BellIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesSolid } from '@heroicons/react/24/solid';
import { userService, HomeData } from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';
import PaymentMethodModal from '../components/PaymentMethodModal';
import AIChatBox from '../components/AIChatBox';
import toast from 'react-hot-toast';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [homeData, setHomeData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const pixCode = '00020126580014br.gov.bcb.pix01360300e793-8b58-41ff-be85-65af61c651c2520400005303986540515.905802BR5901N6001C62170513upmindydayuse6304F74F';

  useEffect(() => {
    fetchHomeData();
  }, []);

  useEffect(() => {
    if (!showAIChat) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowAIChat(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAIChat]);

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      const data = await userService.getHomeData();
      console.log('Home data received:', data);
      console.log('Recent quizzes:', data.recentQuizzes);
      setHomeData(data);
    } catch (error) {
      console.error('Error fetching home data:', error);
      toast.error('Falha ao carregar dados da página inicial');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Combine all resources into a single array
  type ResourceItem = {
    id: string;
    type: 'deck' | 'quiz' | 'match' | 'study';
    title: string;
    description?: string;
    updatedAt: string;
    lastActivityDate?: string;
    link: string;
    playLink?: string;
    count?: number;
    status?: string;
    priority?: string;
  };

  const allResources: ResourceItem[] = [];

  // Add decks
  homeData?.recentDecks.forEach(deck => {
    allResources.push({
      id: deck._id,
      type: 'deck',
      title: deck.name,
      description: deck.description,
      updatedAt: deck.updatedAt,
      lastActivityDate: deck.lastStudied,
      link: `/decks/management/${deck._id}`,
      playLink: `/study/${deck._id}`,
      count: deck.cardCount,
    });
  });

  // Add quizzes
  homeData?.recentQuizzes.forEach(quiz => {
    allResources.push({
      id: quiz._id,
      type: 'quiz',
      title: quiz.title,
      description: quiz.description,
      updatedAt: quiz.updatedAt,
      lastActivityDate: quiz.lastAttempted,
      link: `/quizzes/management/${createSlugWithId(quiz.title, quiz._id)}`,
      playLink: `/quiz/${quiz._id}`,
      count: quiz.questionsCount,
    });
  });

  // Add match games
  homeData?.recentMatchGames.forEach(match => {
    allResources.push({
      id: match._id,
      type: 'match',
      title: match.title,
      description: match.description,
      updatedAt: match.updatedAt,
      lastActivityDate: match.lastAttempted,
      link: `/matches/${createSlugWithId(match.title, match._id)}`,
      playLink: `/matches/${match._id}/play`,
      count: match.pairsCount,
    });
  });

  // Add studies
  homeData?.recentStudies?.forEach(study => {
    allResources.push({
      id: study._id,
      type: 'study',
      title: study.title || study.subject || 'Estudo',
      description: study.description,
      updatedAt: study.updatedAt,
      lastActivityDate: study.start,
      link: `/studies/${study._id}`,
      status: study.status,
      priority: study.priority,
    });
  });

  // Sort by most recent
  allResources.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const getResourceConfig = (type: 'deck' | 'quiz' | 'match' | 'study') => {
    switch (type) {
      case 'deck':
        return {
          icon: RectangleStackIcon,
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          textColor: 'text-blue-600 dark:text-blue-400',
          hoverBorder: 'hover:border-blue-300 dark:hover:border-blue-600',
          label: 'Flashcard',
        };
      case 'quiz':
        return {
          icon: PuzzlePieceIcon,
          bgColor: 'bg-purple-100 dark:bg-purple-900/30',
          textColor: 'text-purple-600 dark:text-purple-400',
          hoverBorder: 'hover:border-purple-300 dark:hover:border-purple-600',
          label: 'Quiz',
        };
      case 'match':
        return {
          icon: SparklesIcon,
          bgColor: 'bg-pink-100 dark:bg-pink-900/30',
          textColor: 'text-pink-600 dark:text-pink-400',
          hoverBorder: 'hover:border-pink-300 dark:hover:border-pink-600',
          label: 'Match',
        };
      case 'study':
        return {
          icon: AcademicCapIcon,
          bgColor: 'bg-teal-100 dark:bg-teal-900/30',
          textColor: 'text-teal-600 dark:text-teal-400',
          hoverBorder: 'hover:border-teal-300 dark:hover:border-teal-600',
          label: 'Estudo',
        };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-full sm:max-w-6xl mx-auto px-0 sm:px-4 lg:px-6 py-4 sm:py-10">
        {/* Quick Access Buttons */}
        <div className="mb-4 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-4 sm:mb-6">
            O que você quer criar?
          </h2>
            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center">
              <Link
                to="/decks"
                className="flex-1 min-w-[140px] sm:min-w-[180px] max-w-[200px] h-[100px] sm:h-[110px] inline-flex flex-col items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center"
              >
                <RectangleStackIcon className="h-5 w-5 sm:h-6 sm:w-6 mb-1" />
                <span className="text-base sm:text-lg font-bold">Flashcards</span>
                <span className="text-xs opacity-90 mt-0.5">Memorize com cartões</span>
              </Link>
              <Link
                to="/quizzes"
                className="flex-1 min-w-[140px] sm:min-w-[180px] max-w-[200px] h-[100px] sm:h-[110px] inline-flex flex-col items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center"
              >
                <PuzzlePieceIcon className="h-5 w-5 sm:h-6 sm:w-6 mb-1" />
                <span className="text-base sm:text-lg font-bold">Quizzes</span>
                <span className="text-xs opacity-90 mt-0.5">Teste seus conhecimentos</span>
              </Link>
              <Link
                to="/matches"
                className="flex-1 min-w-[140px] sm:min-w-[180px] max-w-[200px] h-[100px] sm:h-[110px] inline-flex flex-col items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center"
              >
                <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 mb-1" />
                <span className="text-base sm:text-lg font-bold">Matches</span>
                <span className="text-xs opacity-90 mt-0.5">Jogo de combinação</span>
              </Link>
              <Link
                to="/studies"
                className="flex-1 min-w-[140px] sm:min-w-[180px] max-w-[200px] h-[100px] sm:h-[110px] inline-flex flex-col items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-teal-600 to-cyan-700 hover:from-teal-700 hover:to-cyan-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-center"
              >
                <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 mb-1" />
                <span className="text-base sm:text-lg font-bold">Estudos</span>
                <span className="text-xs opacity-90 mt-0.5">Crie estudos completos com IA</span>
              </Link>
            </div>
        </div>

        {/* Recent Activities Header */}
        <div className="mb-4 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Recente
          </h2>
        </div>

        {/* All Resources Section */}
        {allResources.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-8 sm:p-12 text-center">
            <div className="max-w-2xl mx-auto">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <RectangleStackIcon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Nenhum recurso recente
              </h3>
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                Você ainda não tem recursos de aprendizado. Comece criando seu primeiro deck, quiz ou jogo!
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {allResources.map((resource) => {
              const config = getResourceConfig(resource.type);
              const Icon = config.icon;
              
              return (
                <div
                  key={resource.id}
                  className={`bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-5 sm:p-6 ${config.hoverBorder} hover:shadow-lg transition-all duration-200 flex flex-col min-h-[200px]`}
                >
                  {resource.type === 'match' || resource.type === 'study' ? (
                    <div className="flex items-center space-x-2.5 sm:space-x-3 mb-3 sm:mb-4">
                      <div className={`flex-shrink-0 p-2 sm:p-2.5 ${config.bgColor} rounded-xl`}>
                        <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${config.textColor}`} />
                      </div>
                      <span className={`text-xs sm:text-sm font-semibold ${config.textColor} uppercase tracking-wide`}>
                        {config.label}
                      </span>
                    </div>
                  ) : (
                    <Link
                      to={resource.link}
                      className="flex items-center space-x-2.5 sm:space-x-3 mb-3 sm:mb-4 group"
                    >
                      <div className={`flex-shrink-0 p-2 sm:p-2.5 ${config.bgColor} rounded-xl`}>
                        <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${config.textColor}`} />
                      </div>
                      <span className={`text-xs sm:text-sm font-semibold ${config.textColor} uppercase tracking-wide group-hover:opacity-80 transition-opacity`}>
                        {config.label}
                      </span>
                    </Link>
                  )}
                  
                  <Link
                    to={resource.type === 'match' ? '/matches' : resource.link}
                    className="block group mb-3 sm:mb-4 flex-grow">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2 leading-snug">
                      {resource.title}
                    </h3>
                  </Link>
                  
                  <div className="mt-auto pt-3 sm:pt-4 border-t-2 border-gray-100 dark:border-gray-700">
                    {resource.type !== 'study' && (
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400 font-medium truncate">
                          {resource.count ?? 0} {resource.type === 'deck' ? 'cards' : resource.type === 'quiz' ? 'perguntas' : 'pares'}
                        </span>
                      </div>
                    )}
                    
                    {/* Status for Studies */}
                    {resource.type === 'study' && resource.status && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          resource.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          resource.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          resource.status === 'paused' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          'bg-gray-100 text-gray-700 dark:bg-gray-700/30 dark:text-gray-400'
                        }`}>
                          {resource.status === 'completed' ? 'Concluído' :
                           resource.status === 'in_progress' ? 'Em progresso' :
                           resource.status === 'paused' ? 'Pausado' :
                           'Não iniciado'}
                        </span>
                      </div>
                    )}
                    
                    {/* Last Activity Date */}
                    {resource.lastActivityDate && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-2">
                        <ClockIcon className="h-3.5 w-3.5" />
                        <span>
                          {resource.type === 'deck' ? 'Estudado em' : resource.type === 'study' ? 'Início' : 'Jogado em'}: {formatTime(resource.lastActivityDate)}
                        </span>
                      </div>
                    )}
                    
                    {resource.playLink && (
                      <button
                        onClick={() => navigate(resource.playLink!)}
                        className={`w-full flex items-center justify-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 ${config.bgColor} ${config.textColor} rounded-lg hover:opacity-80 transition-opacity active:scale-95`}
                      >
                        <PlayIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="text-sm sm:text-base font-semibold">
                          {resource.type === 'deck' ? 'Estudar' : 'Jogar'}
                        </span>
                      </button>
                    )}
                    
                    {resource.type === 'study' && (
                      <button
                        onClick={() => navigate(resource.link)}
                        className={`w-full flex items-center justify-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 ${config.bgColor} ${config.textColor} rounded-lg hover:opacity-80 transition-opacity active:scale-95`}
                      >
                        <ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="text-sm sm:text-base font-semibold">
                          Ver detalhes
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {!showAIChat && (
        <button
          type="button"
          onClick={() => setShowAIChat(true)}
          aria-label="Conversar com a IA"
          className="group fixed bottom-6 left-1/2 z-40 inline-flex h-16 w-16 -translate-x-1/2 items-center justify-center rounded-full bg-gradient-to-br from-sky-500/85 via-blue-600/85 to-violet-600/85 text-white shadow-[0_0_20px_3px_rgba(255,255,255,0.35),0_12px_30px_-8px_rgba(79,70,229,0.65)] transition duration-300 hover:-translate-y-1 hover:scale-105 hover:shadow-[0_0_26px_5px_rgba(255,255,255,0.5),0_18px_36px_-8px_rgba(79,70,229,0.75)] active:translate-y-0 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300"
        >
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-white/25 to-transparent" aria-hidden="true" />
          <SparklesSolid className="relative h-7 w-7 drop-shadow-sm transition-transform duration-300 group-hover:rotate-12" aria-hidden="true" />
        </button>
      )}

      {showAIChat && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/60 p-3 backdrop-blur-sm sm:p-6"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setShowAIChat(false);
            }
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-chat-title"
            className="relative flex h-[min(850px,92dvh)] max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-gray-50 p-3 shadow-2xl dark:bg-gray-900 sm:p-6"
          >
            <div className="mb-3 flex flex-shrink-0 items-center justify-between sm:mb-5">
              <h2 id="ai-chat-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Chat com IA
              </h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowAIChat(false)}
                  aria-label="Fechar chat"
                  className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-white"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <AIChatBox showCreationOptions />
            </div>
          </section>
        </div>
      )}
      
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        pixCode={pixCode}
      />
      
    </div>
  );
};

export default Home;
